/**
 * Arrowheads that glide along the dashed part of the monitor's line towards
 * the destination, like the Shipment Desk prototype: the dashes themselves
 * stay still, since in Mapbox GL 3 a changing dash re-lays the line out and
 * keeps it from drawing, and a line-pattern does not draw on the globe at
 * route zoom. Each frame the chevrons are laid out on screen along the line
 * as drawn, so their spacing and direction hold however the camera tilts or
 * turns, then turned back into map positions for a small line layer (lines,
 * not icons: icons fade in on every data change and would flicker).
 */
import type mapboxgl from 'mapbox-gl';
import { wrapLngNear, type LngLat } from './monitorRouteShape';

/** Between two arrowheads along the line (CSS px on screen). */
export const ROUTE_ARROW_SPACING_PX = 110;
/** How fast they move towards the destination (CSS px a second). */
export const ROUTE_ARROW_SPEED_PX_S = 25;
/**
 * Not picked up yet (the planned lane): slower and fainter, so it still
 * reads "this way" while shipments on their way stand out.
 */
export const ROUTE_ARROW_PLANNED_SPEED_PX_S = 12;
export const ROUTE_ARROW_PLANNED_OPACITY = 0.5;
/** From the tip back to the wings, and from the line out to each wing. */
export const ROUTE_ARROW_LENGTH_PX = 6;
export const ROUTE_ARROW_HALF_WIDTH_PX = 5;
/** At most this often (ms): smooth at the speed, light on the map. */
const ROUTE_ARROW_FRAME_MS = 33;

export interface ScreenPoint {
  x: number;
  y: number;
}

/**
 * The chevrons along `line` (screen points, start to end): one every
 * ROUTE_ARROW_SPACING_PX from `offset` px along it, each [left wing, tip,
 * right wing] pointing the way the line goes. None where there is no room
 * for a whole chevron behind its tip.
 */
export const arrowChevrons = (
  line: ScreenPoint[],
  offset: number
): ScreenPoint[][] => {
  const chevrons: ScreenPoint[][] = [];
  let travelled = 0;
  let next =
    ((offset % ROUTE_ARROW_SPACING_PX) + ROUTE_ARROW_SPACING_PX) %
    ROUTE_ARROW_SPACING_PX;
  for (let i = 1; i < line.length; i += 1) {
    const a = line[i - 1];
    const b = line[i];
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    if (!Number.isFinite(length) || length === 0) continue;
    const ux = (b.x - a.x) / length;
    const uy = (b.y - a.y) / length;
    while (next <= travelled + length) {
      if (next >= ROUTE_ARROW_LENGTH_PX) {
        const along = next - travelled;
        const tip = { x: a.x + ux * along, y: a.y + uy * along };
        const back = {
          x: tip.x - ux * ROUTE_ARROW_LENGTH_PX,
          y: tip.y - uy * ROUTE_ARROW_LENGTH_PX,
        };
        // The normal: the line turned a quarter.
        const nx = -uy * ROUTE_ARROW_HALF_WIDTH_PX;
        const ny = ux * ROUTE_ARROW_HALF_WIDTH_PX;
        chevrons.push([
          { x: back.x + nx, y: back.y + ny },
          tip,
          { x: back.x - nx, y: back.y - ny },
        ]);
      }
      next += ROUTE_ARROW_SPACING_PX;
    }
    travelled += length;
  }
  return chevrons;
};

type ArrowMap = Pick<
  mapboxgl.Map,
  'getSource' | 'project' | 'unproject' | 'getLayer' | 'addSource' | 'addLayer'
>;

const EMPTY: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

/**
 * Adds the arrowheads' source and its two layers (an outline in the route's
 * colour under a white stroke), inserted below the layer `beforeId` names
 * (Mapbox's addLayer anchor), or on top of everything without one.
 */
export const addRouteArrowLayers = (
  map: ArrowMap,
  sourceId: string,
  color: string,
  beforeId?: string
) => {
  if (!map.getSource(sourceId))
    map.addSource(sourceId, { type: 'geojson', data: EMPTY });
  const layout = {
    'line-cap': 'round' as const,
    'line-join': 'round' as const,
  };
  // Fainter on a planned lane (see startRouteArrows' `planned`).
  const opacity = [
    'case',
    ['boolean', ['get', 'planned'], false],
    ROUTE_ARROW_PLANNED_OPACITY,
    1,
  ] as mapboxgl.ExpressionSpecification;
  if (!map.getLayer(`${sourceId}-outline`))
    map.addLayer(
      {
        id: `${sourceId}-outline`,
        type: 'line',
        source: sourceId,
        layout,
        paint: {
          'line-color': color,
          'line-width': 5,
          'line-opacity': opacity,
        },
      },
      beforeId
    );
  if (!map.getLayer(sourceId))
    map.addLayer(
      {
        id: sourceId,
        type: 'line',
        source: sourceId,
        layout,
        paint: {
          'line-color': '#ffffff',
          'line-width': 2,
          'line-opacity': opacity,
        },
      },
      beforeId
    );
};

/** Length of a line on screen (px). */
const screenLength = (line: ScreenPoint[]) =>
  line.reduce(
    (total, point, i) =>
      i === 0
        ? 0
        : total + Math.hypot(point.x - line[i - 1].x, point.y - line[i - 1].y),
    0
  );

/** The screen's "as little motion as possible" setting, when it has one. */
const reducedMotionQuery = (): MediaQueryList | null =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

/**
 * A chevron's map coordinates, made continuous around its tip (one shape,
 * never the long way round the world). With `wrap` (the globe, which draws
 * lines within ±180°), a chevron straddling the date line is left out: it is
 * a few pixels wide, and drawn whole it would cross the globe.
 */
export const chevronCoordinates = (
  points: { lng: number; lat: number }[],
  wrap: boolean
): number[][] | null => {
  const tip = points[1];
  const tipLng = wrap ? ((((tip.lng + 180) % 360) + 360) % 360) - 180 : tip.lng;
  const coordinates = points.map(({ lng, lat }) => [
    wrapLngNear(lng, tipLng),
    lat,
  ]);
  if (!coordinates.every((c) => c.every(Number.isFinite))) return null;
  if (wrap && coordinates.some(([lng]) => lng < -180 || lng > 180)) return null;
  return coordinates;
};

/**
 * Moves arrowheads along `line` (the dashed part, ending at the
 * destination) from `startAt` (a requestAnimationFrame time; before it,
 * none, e.g. while the line draws itself in) until the returned stop, which
 * also clears them. `wrap`: on the globe (see chevronCoordinates).
 * `planned`: not picked up yet, so slower and fainter. While the screen asks
 * for less motion (also when that changes as they show), they stay still,
 * following only the camera: before pickup they are the only cue to the
 * way, as the focus circle has no arrow then. The map is updated only when
 * the arrowheads change.
 */
export const startRouteArrows = (
  map: ArrowMap,
  sourceId: string,
  line: LngLat[],
  startAt = 0,
  { wrap = false, planned = false }: { wrap?: boolean; planned?: boolean } = {}
): (() => void) => {
  const setData = (data: GeoJSON.FeatureCollection) =>
    (map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined)?.setData(
      data
    );
  if (line.length < 2) return () => {};
  // The globe: the path within ±180° too, like the line it rides on
  // (adjacent across the date line there, since the globe wraps).
  const path: LngLat[] = wrap
    ? line.map(([lng, lat]) => [((((lng + 180) % 360) + 360) % 360) - 180, lat])
    : line;
  const query = reducedMotionQuery();
  const speed = planned
    ? ROUTE_ARROW_PLANNED_SPEED_PX_S
    : ROUTE_ARROW_SPEED_PX_S;
  let frameId: number | null = null;
  let last = -Infinity;
  // What the map shows now, so an unchanged frame is not sent again.
  let shown = '';
  const show = (data: GeoJSON.FeatureCollection) => {
    const key = JSON.stringify(data);
    if (key === shown) return;
    shown = key;
    setData(data);
  };
  const tick = (now: number) => {
    frameId = window.requestAnimationFrame(tick);
    if (now - last < ROUTE_ARROW_FRAME_MS) return;
    last = now;
    if (now < startAt) {
      show(EMPTY);
      return;
    }
    const screen = path.map((point) => map.project(point));
    // Still: half a spacing in, so the first sits clear of the shipment; on
    // a dashed part shorter than that on screen, in its middle (with room
    // for a whole chevron behind its tip), so there is always one.
    const offset = query?.matches
      ? Math.min(
          ROUTE_ARROW_SPACING_PX / 2,
          Math.max(ROUTE_ARROW_LENGTH_PX, screenLength(screen) / 2)
        )
      : ((now - startAt) / 1000) * speed;
    const features = arrowChevrons(screen, offset).flatMap((chevron) => {
      const coordinates = chevronCoordinates(
        chevron.map((p) => map.unproject([p.x, p.y])),
        wrap
      );
      return coordinates
        ? [
            {
              type: 'Feature' as const,
              properties: { planned },
              geometry: { type: 'LineString' as const, coordinates },
            },
          ]
        : [];
    });
    show({ type: 'FeatureCollection', features });
  };
  frameId = window.requestAnimationFrame(tick);
  return () => {
    if (frameId !== null) window.cancelAnimationFrame(frameId);
    frameId = null;
    shown = '';
    setData(EMPTY);
  };
};
